import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import List from '@mui/material/List';
import { LicenceId } from '@ps/platform-message-framework/common/LicenceId';
import { type Currency, FeatureFlag } from '@ps/web-core-enums';
import type { TrackedTranslation } from '@ps/web-core-intl';
import { useFeature } from '@ps/web-core-store';
import { useTrackers } from '@ps/web-shared-analytics';
import { Translation, useTrackingLabel } from '@ps/web-shared-components';
import classNames from 'clsx';
import { differenceInCalendarDays, isSameMonth } from 'date-fns';
import React, { useCallback, useState } from 'react';
import type { FieldInputProps } from 'react-final-form';
import { Field, useForm } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { lastValueFrom } from 'rxjs';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import ToggleArrow from '@swf/core/assets/icons/arrow.svg';
import WarningIcon from '@swf/core/assets/icons/warning-triangle.svg';
import ErrorIcon from '@swf/core/assets/icons/warning.svg';
import type { Country } from '@swf/core/enums';
import useRequest from '@swf/core/hooks/useRequest';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { FieldNameProp } from '@swf/core/types/Props';

import type {
  FirstWizardData,
  OptionType,
  PaySystem,
  PaySystemDetails,
  PaySystemDetailsResponse,
  SavedCard
} from '../../types/Cashier';
import { CardType } from '../../types/cardType';
import { getCreditTime, trimAsterisks } from '../../utils/cashier';
import PaySystemInfoPrompt from '../Deposit/components/DepositMethod/components/PaySystemInfoPrompt/PaySystemInfoPrompt';
import RiskyInfoPrompt from '../Deposit/components/DepositMethod/components/RiskyInfoPrompt/RiskyInfoPrompt';
import ListItem from '../ListItem/ListItem';
import PaymentMethodLogo from '../PaymentMethodLogo/PaymentMethodLogo';

import {
  TXTWCL_Card_unavailable,
  TXTWCL_Minimum_N0,
  TXTWCL_Your_card_will_expire_in_1_day,
  TXTWCL_Your_card_will_expire_in_N0_da_ELL,
  TXTWCL_Your_card_will_expire_today
} from './PaymentMethodSelect.messages.json';
import {
  accordion,
  accordionBonusReady,
  accordionDetails,
  accordionDisabled,
  accordionExpanded,
  accordionOtherPaymentMethods,
  accordionSummary,
  accordionSummaryContent,
  accordionSummaryDisabled,
  accordionSummaryExpanded,
  avatar,
  avatarContainer,
  button,
  errorIcon,
  list,
  listItem,
  minimum,
  primaryText,
  secondaryText,
  unavailable
} from './PaymentMethodSelect.scss';
import PaymentMethodDaysLeft from './components/PaymentMethodDaysLeft';

interface Props extends FieldNameProp {
  disable?: boolean;
  heading?: TrackedTranslation;
  initialValue?: CardType;
  isSavedCard?: boolean;
  options: (PaySystem | SavedCard)[];
  variant?: 'bonusReady' | 'otherPaymentMethods';
  onClick?: (callback: () => void) => void;
  setNativeBonusVisibility?: () => void;
  isButton?: boolean;
}

const mapOption = (
  option: SavedCard | PaySystem,
  data: FirstWizardData,
  isSavedCard?: boolean
): OptionType => {
  if (isSavedCard) {
    const {
      savedCard: {
        cardInfoId,
        expMonth,
        expYear,
        hostileBin,
        maskedCardNum,
        paySystem,
        specificData
      }
    } = option as SavedCard;

    const { systemName, i18nSystemName, once } = data.paySystems?.find(
      ({ cardType }) => cardType === paySystem
    ) as PaySystem;

    const card: OptionType = {
      cardInfoId,
      cardType: paySystem,
      hostileBin,
      i18nSystemName:
        (paySystem === CardType.OpenBanking && specificData?.[0].bankName) || i18nSystemName,
      maskedCardNum,
      once,
      systemName,
      productId: specificData?.[0].bankId
    };

    if (expMonth && expYear) {
      const expDate = new Date(expYear, expMonth, 0);
      const today = new Date();

      if (isSameMonth(expDate, today)) {
        card.daysLeft = differenceInCalendarDays(expDate, today);
      }
    }

    return card;
  } else {
    const { i18nSystemName, cardType, once, systemName } = option as PaySystem;
    return {
      cardType,
      i18nSystemName,
      once,
      systemName
    };
  }
};

const getDaysLeftMessage = (daysLeft: number) => {
  if (daysLeft === 0) {
    return <Translation {...TXTWCL_Your_card_will_expire_today} />;
  }
  if (daysLeft === 1) {
    return <Translation {...TXTWCL_Your_card_will_expire_in_1_day} />;
  }

  return <Translation {...TXTWCL_Your_card_will_expire_in_N0_da_ELL} values={{ 0: daysLeft }} />;
};

const getSecondaryContext = (
  showMinimumAmount: boolean,
  showCreditTime: boolean,
  option: PaySystem | SavedCard,
  data: FirstWizardData,
  currency?: Currency,
  isSavedCard?: boolean,
  country?: Country
) => {
  let creditTimeMessage;
  const { cardType, daysLeft, hostileBin, once } = mapOption(option, data, isSavedCard);
  if (showCreditTime) {
    creditTimeMessage = getCreditTime(cardType, country);
  }
  if (isSavedCard && hostileBin) {
    return (
      <span className={unavailable}>
        <ErrorIcon className={errorIcon} />
        <Translation {...TXTWCL_Card_unavailable} />
      </span>
    );
  }
  if (showMinimumAmount || showCreditTime || Number.isInteger(daysLeft)) {
    return (
      <>
        {Number.isInteger(daysLeft) && (
          <PaymentMethodDaysLeft
            icon={<WarningIcon />}
            message={getDaysLeftMessage(daysLeft as number)}
          />
        )}
        <span className={minimum}>
          {showMinimumAmount && (
            <Translation
              {...TXTWCL_Minimum_N0}
              values={{
                0: (
                  <CurrencyAmount
                    value={once}
                    currency={currency}
                    currencyDisplay="code"
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                    dollarValue
                  />
                )
              }}
            />
          )}
          {showMinimumAmount && creditTimeMessage && ' | '}
          {creditTimeMessage && <Translation {...creditTimeMessage} />}
        </span>
      </>
    );
  }
  return null;
};

const PaymentMethodSelect: React.FC<Props> = ({
  disable,
  heading,
  fieldName,
  initialValue,
  options,
  variant,
  isSavedCard,
  isButton = true,
  onClick,
  setNativeBonusVisibility
}) => {
  const { localSettings } = useDispatch<Dispatch>();
  const paysystemMessages = useSelector(select.localSettings.getCashier)?.paysystemMessages;
  const { data } = useWizardContext();

  const request = useRequest();
  const form = useForm();
  const currency = useSelector(select.account.getCurrency);
  const license = useSelector(select.account.getLicense);
  const isRomanianTemporaryAccount = useSelector(select.account.isRomanianTemporaryAccount);
  const showMinimumAmount = useFeature(FeatureFlag.SwcDepositMethodMinimum);
  const isSwcMethodVerificationDeposit = useFeature(FeatureFlag.SwcMethodVerificationDeposit);
  const showCreditTime = useFeature(FeatureFlag.SwcCreditTime);
  const { inputFilled } = useTrackers();
  const trackingName = useTrackingLabel(heading) as string;
  const [expanded, setExpanded] = useState(disable);
  const [showInfoPrompt, setShowInfoPrompt] = useState(false);
  const [showRiskyPrompt, setShowRiskyPrompt] = useState(false);
  const [paySystemDetails, setPaySystemDetails] = useState<PaySystemDetails>();
  const country = useSelector(select.account.getCountry) as Country;
  const handleChange = (e: React.ChangeEvent<object>, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  const showPaysystemPrompt = useCallback(
    (paySystemDetails: PaySystemDetails, hostileBin?: boolean) => {
      if (
        paySystemDetails?.msg &&
        paySystemDetails.available &&
        !hostileBin &&
        !(license === LicenceId.ITALY && paySystemDetails.authenticationRequired) &&
        !(isRomanianTemporaryAccount && !paySystemDetails.remainingLimit)
      ) {
        if (paysystemMessages && paysystemMessages[paySystemDetails.type]) {
          if (paysystemMessages[paySystemDetails.type] !== paySystemDetails?.msg) {
            delete paysystemMessages[paySystemDetails.type];
            localSettings.set({
              cashier: {
                paysystemMessages
              }
            });
            return true;
          } else {
            return false;
          }
        } else {
          return true;
        }
      } else {
        return false;
      }
    },
    [isRomanianTemporaryAccount, license, localSettings, paysystemMessages]
  );

  const handleClick = useCallback(
    async (
      input: FieldInputProps<unknown, HTMLInputElement>,
      cardType: CardType,
      strInput?: string,
      cardInfoId?: number,
      hostileBin?: boolean
    ) => {
      if (!isButton) {
        return null;
      }

      input.onChange(cardType);
      if (isSavedCard) {
        data.savedCardInfoId = cardInfoId;
      } else {
        data.regularMethodSelected = true;
      }

      const {
        body: { paySystemDetails: response }
      } = await lastValueFrom(
        request<PaySystemDetailsResponse>({
          service: 'wc3.paysystem.get.paySystemDetails',
          body: { currency, type: cardType }
        })
      );

      const isESRestrictedMethod =
        [
          CardType.Ricarica,
          CardType.MBookOBT,
          CardType.Neosurf,
          CardType.NeosurfHosted,
          CardType.EcoPayZ
        ].includes(cardType) &&
        license === LicenceId.SPAIN &&
        isSwcMethodVerificationDeposit;

      const callback = () => {
        if (isESRestrictedMethod) {
          setShowRiskyPrompt(true);
        } else if (showPaysystemPrompt(response, hostileBin)) {
          setPaySystemDetails(response);
          setShowInfoPrompt(true);
        } else {
          form.submit();
        }
      };

      inputFilled({ fieldName: trackingName, fieldType: 'dropdown', strInput });
      setNativeBonusVisibility?.();

      if (onClick) {
        onClick(callback);
      } else {
        callback();
      }
    },
    [
      isButton,
      isSavedCard,
      request,
      currency,
      inputFilled,
      trackingName,
      setNativeBonusVisibility,
      onClick,
      data,
      showPaysystemPrompt,
      form,
      license,
      isSwcMethodVerificationDeposit
    ]
  );

  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      classes={{
        root: classNames(accordion, {
          [accordionBonusReady]: variant === 'bonusReady',
          [accordionOtherPaymentMethods]: variant === 'otherPaymentMethods'
        }),
        expanded: accordionExpanded,
        disabled: accordionDisabled
      }}
      disabled={disable}
    >
      {heading && (
        <AccordionSummary
          expandIcon={!disable && <ToggleArrow />}
          classes={{
            root: classNames(accordionSummary, {
              [accordionOtherPaymentMethods]: variant === 'otherPaymentMethods'
            }),
            expanded: accordionSummaryExpanded,
            content: accordionSummaryContent,
            disabled: accordionSummaryDisabled
          }}
        >
          <Translation {...heading} />
        </AccordionSummary>
      )}

      {showInfoPrompt
        ? paySystemDetails && (
            <PaySystemInfoPrompt
              paySystemDetails={paySystemDetails}
              callback={() => setShowInfoPrompt(false)}
            />
          )
        : null}
      {showRiskyPrompt ? <RiskyInfoPrompt onclose={() => setShowRiskyPrompt(false)} /> : null}
      <AccordionDetails classes={{ root: accordionDetails }}>
        <List classes={{ root: list }}>
          <Field name={`${fieldName}.type`} initialValue={initialValue}>
            {({ input }) =>
              options.map((option, index) => {
                const {
                  cardType,
                  systemName,
                  i18nSystemName,
                  maskedCardNum,
                  cardInfoId,
                  hostileBin,
                  productId
                } = mapOption(option, data, isSavedCard);
                return (
                  <ListItem
                    key={`${cardType}.${index}`}
                    classes={{
                      button,
                      avatarContainer,
                      avatar,
                      primaryText,
                      root: listItem,
                      secondaryText
                    }}
                    onClick={() => handleClick(input, cardType, systemName, cardInfoId, hostileBin)}
                    avatar={
                      <PaymentMethodLogo
                        cardType={cardType}
                        i18nSystemName={i18nSystemName}
                        productId={productId}
                      />
                    }
                    primary={
                      isSavedCard && maskedCardNum
                        ? `${i18nSystemName} ${trimAsterisks(maskedCardNum)}`
                        : i18nSystemName
                    }
                    secondary={getSecondaryContext(
                      showMinimumAmount,
                      showCreditTime,
                      option,
                      data,
                      currency,
                      isSavedCard,
                      country
                    )}
                    isButton={isButton}
                  />
                );
              })
            }
          </Field>
        </List>
      </AccordionDetails>
    </Accordion>
  );
};

export default PaymentMethodSelect;
